<template>
  <div>
    <transition name="fade">
      <div v-if="open" class="modal-3d">
        <model-viewer
          v-pan-controls
          :src="modelSrc"
          :ios-src="modelIosSrc"
          ar
          ar-scale="fixed"
          camera-controls
          style="min-height: 300px; width: 100%; height: 100%; outline: 0;"
        />
        <b-button variant="link" class="close" @click="$emit('update:open', false)">
          <b-icon icon="x" />
        </b-button>
      </div>
    </transition>
  </div>
</template>

<script>
import "@google/model-viewer"
import { convertConfigurationForModelGen } from "../utils/convert"

const listeners = new WeakMap()

export default {
  props: {
    open: Boolean
  },
  directives: {
    panControls: {
      bind(modelViewer) {
        const tapDistance = 2;
        let panning = false;
        let panX, panY;
        let startX, startY;
        let lastX, lastY;
        let metersPerPixel;

        const startPan = () => {
          const orbit = modelViewer.getCameraOrbit();
          const { theta, phi, radius } = orbit;
          metersPerPixel = 0.75 * radius / modelViewer.getBoundingClientRect().height;
          panX = [-Math.cos(theta), 0, Math.sin(theta)];
          panY = [
            -Math.cos(phi) * Math.sin(theta),
            Math.sin(phi),
            -Math.cos(phi) * Math.cos(theta)
          ];
          modelViewer.interactionPrompt = 'none';
        };

        const movePan = (thisX, thisY) => {
          const dx = (thisX - lastX) * metersPerPixel;
          const dy = (thisY - lastY) * metersPerPixel;
          lastX = thisX;
          lastY = thisY;

          const target = modelViewer.getCameraTarget();
          target.x += dx * panX[0] + dy * panY[0];
          target.y += dx * panX[1] + dy * panY[1];
          target.z += dx * panX[2] + dy * panY[2];
          modelViewer.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;
        };

        const recenter = (pointer) => {
          panning = false;
          if (Math.abs(pointer.clientX - startX) > tapDistance ||
            Math.abs(pointer.clientY - startY) > tapDistance)
            return;
          const rect = modelViewer.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          const hit = modelViewer.positionAndNormalFromPoint(x, y);
          modelViewer.cameraTarget =
            hit == null ? 'auto auto auto' : hit.position.toString();
        };

        const onPointerUp = (event) => {
          const pointer = event.clientX ? event : event.changedTouches[0];
          if (Math.abs(pointer.clientX - startX) < tapDistance &&
            Math.abs(pointer.clientY - startY) < tapDistance) {
            recenter(pointer);
          }
          panning = false;
        };

        modelViewer.addEventListener('mousedown', (event) => {
          startX = event.clientX;
          startY = event.clientY;
          panning = event.button === 2 || event.ctrlKey || event.metaKey ||
            event.shiftKey;
          if (!panning)
            return;

          lastX = startX;
          lastY = startY;
          startPan();
          event.stopPropagation();
        }, true);

        modelViewer.addEventListener('touchstart', (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          panning = event.touches.length === 2;
          if (!panning)
            return;

          const { touches } = event;
          lastX = 0.5 * (touches[0].clientX + touches[1].clientX);
          lastY = 0.5 * (touches[0].clientY + touches[1].clientY);
          startPan();
        }, true);

        modelViewer.addEventListener('mousemove', (event) => {
          if (!panning)
            return;

          movePan(event.clientX, event.clientY);
          event.stopPropagation();
        }, true);

        modelViewer.addEventListener('touchmove', (event) => {
          if (!panning || event.touches.length !== 2)
            return;

          const { touches } = event;
          const thisX = 0.5 * (touches[0].clientX + touches[1].clientX);
          const thisY = 0.5 * (touches[0].clientY + touches[1].clientY);
          movePan(thisX, thisY);
        }, true);

        listeners.set(modelViewer, {
          mouseup: (event) => {
            recenter(event);
          },
          touchend: (event) => {
            if (event.touches.length === 0) {
              recenter(event.changedTouches[0]);
            }
          }
        })
        addEventListener('mouseup', listeners.get(modelViewer).mouseup, true);
        addEventListener('touchend', listeners.get(modelViewer).touchend, true);
      },
      unbind(el) {
        removeEventListener('mouseup', listeners.get(el).mouseup, true);
        removeEventListener('touchend', listeners.get(el).touchend, true);
      }
    }
  },
  computed: {
    modelConfiguration() {
      const json = convertConfigurationForModelGen(
        this.$store.state.modules,
        this.$store.state.configurations.configuration,
        this.$store.state.configurations.configuration.modulePlacements,
        this.$store.state.configurations.emptyModulePlacements,
      )

      return btoa(JSON.stringify(json))
    },
    modelSrc() {
      return `${process.env.VUE_APP_MODEL_GEN_URL}/model.glb?configuration=${this.modelConfiguration}`
    },
    modelIosSrc() {
      return `${process.env.VUE_APP_MODEL_GEN_URL}/model.usdz?configuration=${this.modelConfiguration}`
    },
    // updated() {
    // }
  }
}
</script>

<style lang="scss" scoped>
.modal-3d {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  top: var(--eepos-header-height);
  z-index: 100;
  background: #fff;
}

* {
  outline: 0 !important;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
